import React from "react";
import { PieChart } from "@mui/x-charts-pro/PieChart";
import { BarChart } from "@mui/x-charts-pro/BarChart";
import { LineChart } from "@mui/x-charts-pro/LineChart";
import {
  LIFE_CATS,
  PERSONAS,
  GOAL_PRESETS,
  OFFER_PRESETS,
  TERMS,
  SCENARIOS,
  BUSINESS_SCENARIOS,
  BADGES,
  t,
  isFr,
  catsFor,
  totals,
  award,
  applyScenarioImpact,
  show,
  focusSet,
} from "./state.js";

function Journey({ weekKey }) {
  const current = (weekKey.match(/_(prepare|practice|produce|prove|present)$/) || [])[1] || "prepare";
  const phases = ["prepare", "practice", "produce", "prove", "present"];
  const labels = isFr()
    ? ["Préparer", "Pratiquer", "Produire", "Prouver", "Présenter"]
    : ["Prepare", "Practice", "Produce", "Prove", "Present"];
  const levelName = weekKey.startsWith("money_l2")
    ? t("Run Your Hustle", "Gère ton projet")
    : t("Run Your Month", "Gère ton mois");
  const idx = phases.indexOf(current);
  return (
    <div className="money-journey-wrap">
      <strong>{levelName}</strong>
      <ol className="money-journey" aria-label={t("Money journey", "Parcours argent")}>
        {phases.map((phase, i) => (
          <li key={phase} className={phase === current ? "is-current" : i < idx ? "is-done" : ""}>
            {i + 1}. {labels[i]}
          </li>
        ))}
      </ol>
    </div>
  );
}

function LeftoverMeter({ incomeTotal, left, health }) {
  const pct = incomeTotal > 0 ? Math.max(0, Math.min(100, Math.round((100 * Math.max(left, 0)) / incomeTotal))) : 0;
  const label =
    health === "healthy"
      ? t("Healthy cushion", "Coussin sain")
      : health === "tight"
        ? t("Tight month", "Mois serré")
        : health === "over"
          ? t("Overspent", "Dépassé")
          : t("Add income to start", "Ajoute des revenus pour commencer");
  return (
    <div className={`money-leftover is-${health}`} role="status" aria-live="polite">
      <div className="money-leftover-head">
        <strong>
          {t("Leftover", "Reste")}: ${left.toFixed(0)}
        </strong>
        <span>{label}</span>
      </div>
      <div className="money-leftover-track" aria-hidden="true">
        <div className="money-leftover-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SpendMixChart({ state }) {
  const cats = catsFor(state);
  const rows = cats
    .map((c) => {
      const row = state.categories.find((x) => x.id === c.id);
      return {
        id: c.id,
        label: isFr() ? c.fr : c.en,
        value: Number(row?.amount) || 0,
        kind: row?.kind || "",
      };
    })
    .filter((r) => r.value > 0);
  if (!rows.length) {
    return <p className="app-meta">{t("Fill categories to see your spend mix.", "Remplis les catégories pour voir le mix.")}</p>;
  }
  const need = rows.filter((r) => r.kind === "need").reduce((a, r) => a + r.value, 0);
  const want = rows.filter((r) => r.kind === "want").reduce((a, r) => a + r.value, 0);
  const other = rows.filter((r) => r.kind !== "need" && r.kind !== "want").reduce((a, r) => a + r.value, 0);
  return (
    <div className="money-chart-panel">
      <h3>{t("Spend mix", "Mix de dépenses")}</h3>
      <div className="money-chart-grid">
        <PieChart
          height={220}
          series={[
            {
              data: rows.map((r, i) => ({ id: i, value: r.value, label: r.label })),
              innerRadius: 40,
              paddingAngle: 1,
            },
          ]}
        />
        <BarChart
          height={220}
          xAxis={[{ data: [t("Needs", "Besoins"), t("Wants", "Envies"), t("Untagged", "Sans tag")], scaleType: "band" }]}
          series={[{ data: [need, want, other], label: t("Dollars", "Dollars") }]}
          margin={{ top: 16, bottom: 32, left: 40, right: 8 }}
        />
      </div>
    </div>
  );
}

function BreakEvenChart({ business }) {
  const price = Number(business.price) || 0;
  const materials = Number(business.materialCost) || 0;
  const fixed = Number(business.fixedCost) || 0;
  const margin = price - materials;
  const breakEven = margin > 0 ? Math.ceil(fixed / margin) : null;
  const salesMax = Math.max(12, (breakEven || 5) * 2, Number(business.sales) || 5);
  const xs = Array.from({ length: salesMax + 1 }, (_, i) => i);
  const profit = xs.map((s) => (price - materials) * s - fixed);
  return (
    <div className="money-chart-panel">
      <h3>{t("Break-even curve", "Courbe du seuil")}</h3>
      <p className="app-meta">
        {breakEven == null
          ? t("Raise price above materials to find break-even.", "Monte le prix au-dessus des matériaux pour trouver le seuil.")
          : t(`Break-even ≈ ${breakEven} sales`, `Seuil ≈ ${breakEven} ventes`)}
      </p>
      <LineChart
        height={240}
        xAxis={[{ data: xs, label: t("Sales", "Ventes") }]}
        series={[{ data: profit, label: t("Profit after setup", "Profit après départ"), area: true }]}
        margin={{ top: 16, bottom: 40, left: 48, right: 16 }}
      />
    </div>
  );
}

function ForecastChart({ left }) {
  const base = Math.max(0, left);
  const data = [
    { label: t("Optimistic", "Optimiste"), value: Math.round(base * 1.25) },
    { label: t("OK", "OK"), value: Math.round(base) },
    { label: t("Slow", "Lent"), value: Math.round(base * 0.45) },
  ];
  return (
    <div className="money-chart-panel">
      <h3>{t("3-month leftover sketch", "Esquisse reste sur 3 mois")}</h3>
      <BarChart
        height={220}
        xAxis={[{ data: data.map((d) => d.label), scaleType: "band" }]}
        series={[{ data: data.map((d) => d.value), label: t("Leftover $", "Reste $") }]}
        margin={{ top: 16, bottom: 32, left: 40, right: 8 }}
      />
    </div>
  );
}

export default function MoneyLabApp({ state, setState, weekKey, canWrite, toast }) {
  const focus = focusSet(weekKey);
  const fr = isFr();
  const { incomeTotal, spendTotal, left, budgetHealth } = totals(state);
  const currentPhase = (weekKey.match(/_(prepare|practice|produce|prove|present)$/) || [])[1] || "prepare";

  const update = (patch) => {
    setState((prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      next.labOpened = true;
      const tot = totals(next);
      next.budgetHealth = tot.budgetHealth;
      return next;
    });
  };

  const applySample = (personaId) => {
    void personaId;
  };
  void applySample;

  const completeProfile = () => {
    const goal = GOAL_PRESETS.find((item) => item.id === state.profile.goal);
    update((prev) => {
      const profile = {
        ...prev.profile,
        goalLabel: goal ? (fr ? goal.fr : goal.en) : prev.profile.goalLabel,
        goalAmount: Number(prev.profile.goalAmount) || goal?.amount || 0,
        complete: !!(prev.profile.persona && prev.profile.goal),
      };
      let next = { ...prev, profile };
      if (profile.complete && profile.mode === "sample") {
        const persona = PERSONAS.find((item) => item.id === profile.persona) || PERSONAS[0];
        next.income = [{ id: "sample_income", label: t("Monthly money in", "Argent mensuel reçu"), amount: persona.sample.income }];
        next.categories = LIFE_CATS.map((cat) => ({
          id: cat.id,
          amount: Number(persona.sample[cat.id]) || 0,
          kind: ["phone", "food", "transit", "other"].includes(cat.id) ? "need" : "want",
        }));
        next.driveSet = true;
      }
      if (profile.complete && goal && !(prev.goals || []).length) {
        next.goals = [{ name: profile.goalLabel, target: profile.goalAmount, current: 0 }];
      }
      return next;
    });
    toast(t("Mission started — watch the leftover meter.", "Mission lancée — regarde le reste."));
  };

  const cats = catsFor(state);
  const persona = PERSONAS.find((item) => item.id === state.profile.persona);

  return (
    <div className="yc-lab money-lab money-lab-react app-panel" data-phase={weekKey}>
      <p className="eyebrow">
        {t("Money lab", "Labo argent")} · {(weekKey || "").replace(/^money_/, "").replace(/_/g, " · ")}
      </p>
      <h2>{t("Your month, your rules", "Ton mois, tes règles")}</h2>
      <p className="lede">
        {t(
          "One job at a time. Honesty beats a perfect forecast. Parent owns any credit card.",
          "Une job à la fois. L’honnêteté bat une prévision parfaite. Le parent possède toute carte."
        )}
      </p>

      <Journey weekKey={weekKey} />

      {(show(focus, "onboarding") || !state.profile?.complete) && (
        <section className="money-setup" aria-labelledby="money-setup-title">
          <p className="eyebrow">{t("Choose your mission", "Choisis ta mission")}</p>
          <h3 id="money-setup-title">{t("Make the numbers feel like your life", "Des chiffres qui ressemblent à ta vie")}</h3>
          <div className="money-choice-grid">
            {PERSONAS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`money-choice${state.profile.persona === p.id ? " is-selected" : ""}`}
                aria-pressed={state.profile.persona === p.id}
                disabled={!canWrite}
                onClick={() => update((s) => ({ ...s, profile: { ...s.profile, persona: p.id } }))}
              >
                <strong>
                  {p.ages} · {fr ? p.fr : p.en}
                </strong>
                <span>{fr ? p.frHint : p.enHint}</span>
              </button>
            ))}
          </div>
          <select
            className="money-goal-select"
            disabled={!canWrite}
            value={state.profile.goal || ""}
            aria-label={t("Choose a money goal", "Choisis un objectif argent")}
            onChange={(e) => update((s) => ({ ...s, profile: { ...s.profile, goal: e.target.value } }))}
          >
            <option value="">{t("Choose your goal…", "Choisis ton objectif…")}</option>
            {GOAL_PRESETS.map((g) => (
              <option key={g.id} value={g.id}>
                {fr ? g.fr : g.en}
              </option>
            ))}
          </select>
          <div className="money-setup-options">
            <select
              disabled={!canWrite}
              value={state.profile.mode}
              onChange={(e) => update((s) => ({ ...s, profile: { ...s.profile, mode: e.target.value } }))}
            >
              <option value="sample">{t("Private sample numbers", "Chiffres exemples privés")}</option>
              <option value="real">{t("My rounded numbers", "Mes chiffres arrondis")}</option>
            </select>
            <select
              disabled={!canWrite}
              value={String(state.profile.session || 15)}
              onChange={(e) => update((s) => ({ ...s, profile: { ...s.profile, session: Number(e.target.value) } }))}
            >
              {[10, 15, 20].map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn primary"
            disabled={!(canWrite && state.profile.persona && state.profile.goal)}
            onClick={completeProfile}
          >
            {t("Start my money mission", "Commencer ma mission argent")}
          </button>
        </section>
      )}

      {state.profile?.complete && (
        <div className="money-profile-summary">
          <span>
            {persona?.ages || ""} · {fr ? persona?.fr : persona?.en}
          </span>
          <strong>{state.profile.goalLabel || t("My money goal", "Mon objectif argent")}</strong>
          <span>
            {state.profile.session || 15} min ·{" "}
            {state.profile.mode === "sample" ? t("sample mode", "mode exemple") : t("private real mode", "mode réel privé")}
          </span>
        </div>
      )}

      <div className="yc-lab-xp">
        <strong>XP {state.xp || 0}</strong>
        <span>
          {(state.badges || []).map((b) => BADGES[b]?.[fr ? "fr" : "en"] || b).join(" · ") ||
            t("Earn badges as you clear phases", "Gagne des badges en nettoyant les phases")}
        </span>
      </div>

      {show(focus, "terms") && (
        <section className="money-terms">
          <h3>{t("Flip the money terms", "Retourne les termes argent")}</h3>
          <div className="money-term-grid path-term-grid" data-term-grid>
            {TERMS.map((term) => {
              const flipped = (state.termsFlipped || []).includes(term.id);
              const copy = fr ? term.fr : term.en;
              return (
                <button
                  key={term.id}
                  type="button"
                  className={`path-term-card money-term-card${flipped ? " is-flipped" : ""}`}
                  aria-pressed={flipped}
                  onClick={() => {
                    update((s) => {
                      const set = new Set(s.termsFlipped || []);
                      if (set.has(term.id)) set.delete(term.id);
                      else set.add(term.id);
                      const termsFlipped = [...set];
                      const next = { ...s, termsFlipped };
                      if (termsFlipped.length >= TERMS.length) {
                        next.termsQuizOk = true;
                        award(next, "terms_clear", 10);
                      }
                      return next;
                    });
                  }}
                >
                  <span className="path-term-face is-front money-term-face">{copy.term}</span>
                  <span className="path-term-face is-back money-term-face">{copy.def}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {show(focus, "meter") && state.profile?.complete && (
        <>
          <div className="money-dashboard">
            {[
              [t("Available", "Disponible"), `$${left.toFixed(0)}`],
              [t("Spent", "Dépensé"), `$${spendTotal.toFixed(0)}`],
              [
                t("Cushion", "Coussin"),
                budgetHealth === "healthy"
                  ? t("Ready", "Prêt")
                  : budgetHealth === "tight"
                    ? t("Thin", "Mince")
                    : budgetHealth === "over"
                      ? t("Over", "Dépassé")
                      : t("Build it", "À créer"),
              ],
            ].map(([label, value]) => (
              <div key={label} className="money-stat">
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
          <LeftoverMeter incomeTotal={incomeTotal} left={left} health={budgetHealth} />
        </>
      )}

      {show(focus, "income") && state.profile?.complete && (
        <section>
          <h3>{t("Money in this month", "Argent ce mois-ci")}</h3>
          {(state.income || []).map((row, i) => (
            <div key={row.id || i} className="money-income-row">
              <input
                type="text"
                disabled={!canWrite}
                value={row.label}
                onChange={(e) => {
                  const income = [...state.income];
                  income[i] = { ...income[i], label: e.target.value };
                  update({ income });
                }}
              />
              <input
                type="number"
                min="0"
                step="1"
                disabled={!canWrite}
                value={row.amount}
                onChange={(e) => {
                  const income = [...state.income];
                  income[i] = { ...income[i], amount: Math.max(0, Number(e.target.value) || 0) };
                  update({ income });
                }}
              />
            </div>
          ))}
        </section>
      )}

      {(show(focus, "map") || show(focus, "drive")) && state.profile?.complete && (
        <section>
          <h3>{t("Map the month", "Cartographie le mois")}</h3>
          <p className="app-meta money-map-hint">
            {t(
              "For each category, type about how many dollars you spend in a typical month (round is fine — e.g. 25, not 24.87). Then tag Need or Want.",
              "Pour chaque catégorie, inscris environ combien de dollars tu dépenses dans un mois typique (arrondi OK — ex. 25, pas 24,87). Puis tague Besoin ou Envie."
            )}
          </p>
          <label className="age-check">
            <input
              type="checkbox"
              checked={!!state.drives}
              disabled={!canWrite}
              onChange={(e) => update({ drives: e.target.checked, driveSet: true })}
            />
            <span>{t("I drive or will soon (unlock gas / insurance / car)", "Je conduis ou bientôt (débloque essence / assurance / auto)")}</span>
          </label>
          <div className="money-cat-grid">
            {cats.map((c) => {
              const row = state.categories.find((x) => x.id === c.id) || { id: c.id, amount: 0, kind: "" };
              return (
                <div key={c.id} className="money-cat-card">
                  <strong>{fr ? c.fr : c.en}</strong>
                  <label className="money-amount-field">
                    <span className="money-amount-label">{t("Monthly $ amount", "Montant $ / mois")}</span>
                    <div className="money-input-with-unit">
                      <span aria-hidden="true">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        disabled={!canWrite}
                        value={row.amount}
                        aria-label={`${fr ? c.fr : c.en} — ${t("dollars per month", "dollars par mois")}`}
                        placeholder="0"
                        onChange={(e) => {
                          const amount = Math.max(0, Number(e.target.value) || 0);
                          update((s) => {
                            const categories = [...s.categories];
                            const idx = categories.findIndex((x) => x.id === c.id);
                            if (idx >= 0) categories[idx] = { ...categories[idx], amount };
                            else categories.push({ id: c.id, amount, kind: "" });
                            return { ...s, categories };
                          });
                        }}
                      />
                    </div>
                  </label>
                  <select
                    disabled={!canWrite}
                    value={row.kind || ""}
                    aria-label={`${fr ? c.fr : c.en} — ${t("need or want", "besoin ou envie")}`}
                    onChange={(e) => {
                      const kind = e.target.value;
                      update((s) => {
                        const categories = [...s.categories];
                        const idx = categories.findIndex((x) => x.id === c.id);
                        if (idx >= 0) categories[idx] = { ...categories[idx], kind };
                        else categories.push({ id: c.id, amount: 0, kind });
                        return { ...s, categories };
                      });
                    }}
                  >
                    <option value="">{t("Need or want?", "Besoin ou envie?")}</option>
                    <option value="need">{t("Need", "Besoin")}</option>
                    <option value="want">{t("Want", "Envie")}</option>
                  </select>
                </div>
              );
            })}
          </div>
          {canWrite && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                update((s) => {
                  const next = { ...s, budgetSaved: true };
                  award(next, "first_budget", 15);
                  return next;
                });
                toast(t("Month map saved · +15 XP", "Carte du mois sauvée · +15 XP"));
              }}
            >
              {t("Save this month map", "Sauver cette carte du mois")}
            </button>
          )}
        </section>
      )}

      {(show(focus, "chart") || show(focus, "map")) && state.profile?.complete && <SpendMixChart state={state} />}

      {show(focus, "goals") && state.profile?.complete && (
        <section>
          <h3>{t("Three spending priorities", "Trois priorités de dépenses")}</h3>
          {[0, 1, 2].map((i) => (
            <input
              key={i}
              type="text"
              disabled={!canWrite}
              placeholder={`${t("Priority", "Priorité")} ${i + 1}`}
              value={state.priorities?.[i] || ""}
              onChange={(e) => {
                const priorities = [...(state.priorities || ["", "", ""])];
                priorities[i] = e.target.value;
                update({ priorities });
              }}
            />
          ))}
        </section>
      )}

      {show(focus, "scenarios") && state.profile?.complete && (
        <section>
          <h3>
            {currentPhase === "prove"
              ? t("Life Happens · survive two surprises", "La vie arrive · survis à deux surprises")
              : t("What-if challenge", "Défi et si")}
          </h3>
          <p className="app-meta">
            {t(
              "Each choice rewrites leftover. Undo anytime. Explain the trade-off.",
              "Chaque choix réécrit le reste. Annule quand tu veux. Explique le compromis."
            )}
          </p>
          {SCENARIOS.slice(0, currentPhase === "prove" ? 3 : 2).map((sc, scenarioIndex) => {
            const done = (state.scenariosDone || []).includes(sc.id);
            return (
              <div key={sc.id} className={`yc-scenario${done ? " is-done" : ""}`}>
                <p className="yc-scenario-number">
                  {t("Event", "Événement")} {scenarioIndex + 1}
                </p>
                <p>{fr ? sc.fr : sc.en}</p>
                {done ? (
                  <>
                    <p className="money-consequence">
                      {t("Your move: ", "Ton choix : ")}
                      {(() => {
                        const choice = sc.choices.find((item) => item.id === state.scenarioAnswers?.[sc.id]);
                        return choice ? (fr ? choice.fr : choice.en) : t("decision saved", "décision sauvée");
                      })()}
                    </p>
                    <p className="money-consequence money-consequence-impact">
                      {t("Leftover now", "Reste maintenant")}: ${left.toFixed(0)} ({budgetHealth})
                    </p>
                    <textarea
                      rows={2}
                      disabled={!canWrite}
                      placeholder={t(
                        "Why does this trade-off work for you? What changes next?",
                        "Pourquoi ce compromis fonctionne pour toi? Qu’est-ce qui change ensuite?"
                      )}
                      value={state.scenarioReflections?.[sc.id] || ""}
                      onChange={(e) => {
                        update((s) => {
                          const scenarioReflections = { ...s.scenarioReflections, [sc.id]: e.target.value };
                          const next = { ...s, scenarioReflections };
                          if (Object.values(scenarioReflections).filter((v) => String(v).trim()).length >= 2) {
                            award(next, "crash_clear", 20);
                          }
                          return next;
                        });
                      }}
                    />
                    {canWrite && (
                      <button
                        type="button"
                        className="btn text"
                        onClick={() => {
                          update((s) => {
                            const scenariosDone = (s.scenariosDone || []).filter((id) => id !== sc.id);
                            const scenarioAnswers = { ...s.scenarioAnswers };
                            const scenarioReflections = { ...s.scenarioReflections };
                            const scenarioImpacts = { ...s.scenarioImpacts };
                            delete scenarioAnswers[sc.id];
                            delete scenarioReflections[sc.id];
                            delete scenarioImpacts[sc.id];
                            return { ...s, scenariosDone, scenarioAnswers, scenarioReflections, scenarioImpacts };
                          });
                        }}
                      >
                        {t("Retry this challenge", "Rejouer ce défi")}
                      </button>
                    )}
                  </>
                ) : (
                  canWrite &&
                  sc.choices.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      className="btn ghost"
                      onClick={() => {
                        update((s) => {
                          let next = applyScenarioImpact(s, sc.id, ch);
                          next.scenariosDone = [...(next.scenariosDone || []), sc.id];
                          next.scenarioAnswers = { ...next.scenarioAnswers, [sc.id]: ch.id };
                          award(next, null, ch.xp);
                          award(next, "scenario", 0);
                          return next;
                        });
                        toast(t(`Decision saved · leftover updated · +${ch.xp} XP`, `Décision sauvée · reste mis à jour · +${ch.xp} XP`));
                      }}
                    >
                      {fr ? ch.fr : ch.en}
                    </button>
                  ))
                )}
              </div>
            );
          })}
        </section>
      )}

      {(show(focus, "crash") || show(focus, "weeklog")) && (
        <section>
          <h3>{t("Prove it", "Prouve-le")}</h3>
          <input
            type="text"
            disabled={!canWrite}
            placeholder={t("One flexible category I’d cut…", "Une catégorie flexible que je couperais…")}
            value={state.flexibleCut || ""}
            onChange={(e) => update({ flexibleCut: e.target.value })}
          />
          <label className="age-check">
            <input
              type="checkbox"
              checked={!!state.parentPayOk}
              disabled={!canWrite}
              onChange={(e) => update({ parentPayOk: e.target.checked })}
            />
            <span>{t("Parent checked my pay-yourself-first line", "Parent a vérifié ma ligne se-payer-d’abord")}</span>
          </label>
          <textarea
            rows={3}
            disabled={!canWrite}
            placeholder={t("Week spend log (snacks, transit, games…)", "Journal semaine (collations, transit, jeux…)")}
            value={state.weekLog || ""}
            onChange={(e) => update({ weekLog: e.target.value })}
          />
        </section>
      )}

      {show(focus, "ontario") && (
        <details className="coach-stuck">
          <summary>{t("Ontario money tips (not advice)", "Trucs argent Ontario (pas un conseil)")}</summary>
          <p>
            {t(
              "Allowance is informal. Student jobs may get a T4 later — keep paystubs. TFSA is for 18+. This app is practice, not a bank.",
              "L’allocation est informelle. Un emploi étudiant peut donner un T4 plus tard — garde les talons. CELI à 18+. Ceci est de la pratique, pas une banque."
            )}
          </p>
        </details>
      )}

      {(show(focus, "business") || show(focus, "pricing")) && (
        <section>
          <h3>{t("Run Your Hustle · pricing simulator", "Gère ton projet · simulateur de prix")}</h3>
          <div className="money-offer-grid">
            {OFFER_PRESETS.map((preset) => (
              <button
                key={preset.en}
                type="button"
                className="money-choice"
                disabled={!canWrite}
                onClick={() => {
                  update((s) => {
                    const business = {
                      ...s.business,
                      offer: fr ? preset.fr : preset.en,
                      materialCost: preset.materials,
                      minutes: preset.minutes,
                      price: preset.price,
                    };
                    const next = { ...s, business };
                    award(next, "biz_start", 10);
                    return next;
                  });
                }}
              >
                <strong>{fr ? preset.fr : preset.en}</strong>
                <span>
                  ${preset.price} · {preset.minutes} min
                </span>
              </button>
            ))}
          </div>
          <input
            type="text"
            disabled={!canWrite}
            placeholder={t("Or type your tiny offer", "Ou écris ta mini-offre")}
            value={state.business?.offer || ""}
            onChange={(e) => update((s) => ({ ...s, business: { ...s.business, offer: e.target.value } }))}
          />
          <div className="money-pricing-grid">
            {[
              ["price", t("Price per sale", "Prix par vente"), "$"],
              ["materialCost", t("Materials per sale", "Matériaux par vente"), "$"],
              ["fixedCost", t("One-time setup costs", "Coûts de départ uniques"), "$"],
              ["minutes", t("Minutes per sale", "Minutes par vente"), "min"],
              ["sales", t("Expected sales", "Ventes prévues"), t("sales", "ventes")],
            ].map(([key, label, suffix]) => (
              <label key={key}>
                <span>{label}</span>
                <div className="money-input-with-unit">
                  <input
                    type="number"
                    min={key === "minutes" || key === "sales" ? 1 : 0}
                    step="1"
                    disabled={!canWrite}
                    value={Number(state.business?.[key]) || 0}
                    onChange={(e) =>
                      update((s) => ({
                        ...s,
                        business: { ...s.business, [key]: Math.max(0, Number(e.target.value) || 0) },
                      }))
                    }
                  />
                  <span>{suffix}</span>
                </div>
              </label>
            ))}
          </div>
          {(() => {
            const priceNum = Number(state.business.price) || 0;
            const materialNum = Number(state.business.materialCost) || 0;
            const minutesNum = Math.max(1, Number(state.business.minutes) || 60);
            const salesNum = Math.max(1, Number(state.business.sales) || 1);
            const revenue = priceNum * salesNum;
            const profit = (priceNum - materialNum) * salesNum;
            const hourly = ((priceNum - materialNum) * 60) / minutesNum;
            const fixedCost = Number(state.business.fixedCost) || 0;
            const margin = priceNum - materialNum;
            const breakEven = margin > 0 ? Math.ceil(fixedCost / margin) : null;
            return (
              <>
                <div className="money-business-results">
                  {[
                    [t("Revenue", "Revenus"), `$${revenue.toFixed(0)}`],
                    [t("Profit after materials", "Profit après matériaux"), `$${profit.toFixed(0)}`],
                    [t("Pay for your time", "Paie pour ton temps"), `$${hourly.toFixed(2)}/h`],
                    [
                      t("Break-even", "Seuil"),
                      breakEven == null ? t("Not possible yet", "Pas encore possible") : `${breakEven} ${t("sales", "ventes")}`,
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="money-stat">
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <BreakEvenChart business={state.business} />
                {canWrite && (
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={priceNum <= 0}
                    onClick={() => {
                      update((s) => {
                        const priceTests = [
                          ...(s.business.priceTests || []),
                          { price: priceNum, materials: materialNum, fixed: fixedCost, minutes: minutesNum, sales: salesNum, profit, hourly, at: Date.now() },
                        ].slice(-3);
                        const next = { ...s, business: { ...s.business, priceTests } };
                        award(next, null, 8);
                        if (priceTests.length >= 3) award(next, "biz_start", 0);
                        return next;
                      });
                      toast(t("Price experiment saved · +8 XP", "Expérience de prix sauvegardée · +8 XP"));
                    }}
                  >
                    {t("Save this price test", "Sauvegarder ce test de prix")} ({(state.business.priceTests || []).length}/3)
                  </button>
                )}
              </>
            );
          })()}
          <textarea
            rows={3}
            disabled={!canWrite}
            placeholder={t("Cash-in / cash-out log: date · what · amount · in or out", "Journal cash : date · quoi · montant · entrée ou sortie")}
            value={state.business?.books || ""}
            onChange={(e) => update((s) => ({ ...s, business: { ...s.business, books: e.target.value } }))}
          />
        </section>
      )}

      {show(focus, "bizscenarios") && (
        <section>
          <h3>{t("Business pressure tests", "Tests de pression business")}</h3>
          {BUSINESS_SCENARIOS.map((scenario) => {
            const done = (state.business.scenariosDone || []).includes(scenario.id);
            return (
              <div key={scenario.id} className={`yc-scenario${done ? " is-done" : ""}`}>
                <p>{fr ? scenario.fr : scenario.en}</p>
                {!done && canWrite && (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      update((s) => {
                        const next = {
                          ...s,
                          breakEvenOk: true,
                          business: {
                            ...s.business,
                            scenariosDone: [...(s.business.scenariosDone || []), scenario.id],
                          },
                        };
                        award(next, "breakeven", 25);
                        return next;
                      });
                      toast(t("Pressure test logged · +25 XP", "Test de pression noté · +25 XP"));
                    }}
                  >
                    {t("Run this scenario", "Jouer ce scénario")}
                  </button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {show(focus, "forecast") && state.profile?.complete && <ForecastChart left={left} />}

      {show(focus, "share") && state.profile?.complete && (
        <section className="money-share-card">
          <h3>{t("My 60-second Money Pitch", "Mon pitch argent 60 secondes")}</h3>
          <p className="app-meta">{t("My month share card", "Carte partage mon mois")}</p>
          <p>
            {t("Leftover", "Reste")}: <strong>${left.toFixed(0)}</strong> · {budgetHealth}
          </p>
          <p>
            {t("Priorities", "Priorités")}: {(state.priorities || []).filter(Boolean).join(" · ") || "—"}
          </p>
          <textarea
            rows={2}
            disabled={!canWrite}
            placeholder={t("One habit to keep next month…", "Une habitude à garder le mois prochain…")}
            value={state.habit || ""}
            onChange={(e) => update({ habit: e.target.value })}
          />
          {canWrite && (
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                const text = `${t("Money Story", "Histoire argent")}\n${t("Leftover", "Reste")}: $${left.toFixed(0)}\n${t("Habit", "Habitude")}: ${state.habit || "—"}`;
                navigator.clipboard?.writeText(text);
                update({ planCopied: true, bizShowDone: weekKey.startsWith("money_l2") });
                if (weekKey.startsWith("money_l2")) {
                  update((s) => {
                    const next = { ...s, bizShowDone: true };
                    return next;
                  });
                }
                toast(t("Share card copied", "Carte copiée"));
              }}
            >
              {t("Copy share card", "Copier la carte")}
            </button>
          )}
          {weekKey.startsWith("money_l2") && (
            <input
              type="text"
              disabled={!canWrite}
              placeholder={t("One change for next sell…", "Un changement pour la prochaine vente…")}
              value={state.business?.nextChange || ""}
              onChange={(e) => update((s) => ({ ...s, business: { ...s.business, nextChange: e.target.value } }))}
            />
          )}
        </section>
      )}

      {show(focus, "feedback") && state.profile?.complete && (
        <p className="money-consequence">
          {budgetHealth === "over"
            ? t("Overspent — pick one want to cut before touching needs.", "Dépassé — coupe une envie avant les besoins.")
            : budgetHealth === "tight"
              ? t("Thin cushion — try a $10 pay-yourself-first line.", "Coussin mince — essaie 10 $ se-payer-d’abord.")
              : budgetHealth === "healthy"
                ? t("Month still breathes. Name one habit to keep.", "Le mois respire. Nomme une habitude à garder.")
                : t("Add income and a few categories to see leftover move.", "Ajoute revenus et catégories pour voir le reste bouger.")}
        </p>
      )}
    </div>
  );
}
